import { useState, useEffect, useCallback } from 'react';
import { securityAPI } from '../../../services/api';

export interface SecurityEvent {
  id: number;
  event_type: string;
  event_type_display: string;
  severity: string;
  severity_display: string;
  user: number | null;
  username: string;
  ip_address: string | null;
  user_agent: string;
  path: string;
  details: Record<string, any>;
  resolved: boolean;
  resolved_by: number | null;
  resolved_by_username: string | null;
  resolved_at: string | null;
  timestamp: string;
}

export interface SecuritySummary {
  total_events: number;
  unresolved: number;
  last_24h: number;
  last_7d: number;
  severity_24h: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  top_event_types_24h: { event_type: string; count: number }[];
  top_ips_24h: { ip_address: string; count: number }[];
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SecurityEvent[];
}

export function useAdminSecurityEvents() {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [choices, setChoices] = useState<{
    event_types: [string, string][];
    severities: [string, string][];
  }>({ event_types: [], severities: [] });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ totalCount: 0, currentPage: 1 });
  const { totalCount, currentPage } = pagination;

  const [filters, setFilters] = useState({
    filterEventType: '',
    filterSeverity: '',
    filterResolved: '',
    filterDateFrom: '',
    filterDateTo: '',
    filterSearch: '',
  });

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const fetchEvents = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: page.toString() };
      if (filters.filterEventType) params.event_type = filters.filterEventType;
      if (filters.filterSeverity) params.severity = filters.filterSeverity;
      if (filters.filterResolved) params.resolved = filters.filterResolved;
      if (filters.filterDateFrom) params.date_from = filters.filterDateFrom;
      if (filters.filterDateTo) params.date_to = filters.filterDateTo;
      if (filters.filterSearch) params.search = filters.filterSearch;

      const response = await securityAPI.getEvents(params);
      const data = response.data as PaginatedResponse;
      setEvents(data.results);
      setPagination({ totalCount: data.count, currentPage: page });
    } catch (err) {
      console.error('Failed to fetch security events:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await securityAPI.getSummary();
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch security summary:', err);
    }
  }, []);

  const fetchChoices = useCallback(async () => {
    try {
      const response = await securityAPI.getChoices();
      setChoices(response.data);
    } catch (err) {
      console.error('Failed to fetch choices:', err);
    }
  }, []);

  useEffect(() => {
    fetchChoices();
    fetchSummary();
  }, [fetchChoices, fetchSummary]);

  useEffect(() => {
    fetchEvents(1);
  }, [fetchEvents]);

  const resolveEvent = async (id: number) => {
    try {
      const response = await securityAPI.resolveEvent(id);
      setEvents(prev => prev.map(e => e.id === id ? response.data : e));
      fetchSummary();
    } catch (err) {
      console.error('Failed to resolve event:', err);
    }
  };

  const unresolveEvent = async (id: number) => {
    try {
      const response = await securityAPI.unresolveEvent(id);
      setEvents(prev => prev.map(e => e.id === id ? response.data : e));
      fetchSummary();
    } catch (err) {
      console.error('Failed to unresolve event:', err);
    }
  };

  const bulkResolve = async () => {
    if (selectedIds.size === 0) return;
    try {
      await securityAPI.bulkResolve(Array.from(selectedIds));
      setSelectedIds(new Set());
      fetchEvents(currentPage);
      fetchSummary();
    } catch (err) {
      console.error('Failed to bulk resolve:', err);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const unresolvedIds = events.filter(e => !e.resolved).map(e => e.id);
    const allSelected = unresolvedIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(unresolvedIds));
    }
  };

  const clearFilters = () => {
    setFilters({
      filterEventType: '',
      filterSeverity: '',
      filterResolved: '',
      filterDateFrom: '',
      filterDateTo: '',
      filterSearch: '',
    });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }) + ' ' + d.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const formatTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  const totalPages = Math.ceil(totalCount / 50);

  return {
    events, summary, choices, loading,
    totalCount, currentPage, totalPages,
    filters, setFilters,
    expandedId, setExpandedId,
    selectedIds, toggleSelect, toggleSelectAll,
    fetchEvents, fetchSummary, resolveEvent, unresolveEvent, bulkResolve,
    clearFilters, formatDate, formatTimeAgo,
  };
}
